import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider
} from '@mui/material';

//project imports
import {
  AppModule,
  InputType,
  ValidationPatterns,
  YupType
} from 'utils/constants';
import { createFormControlProps, createValidationSchema } from 'utils';
import Loader from 'components/loading/Loader';
import FormControl from 'components/forms/Form/FormControl';
import { GDSForDeliveryPointNameInterface } from 'features/eai/eaiTypes';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { registerGDSToDeliveryPoint } from 'features/eai/thunks/deliveryPointThunks';
import { selectEaiEtagList } from 'features/eai/selectors';

// types
import { FormInput } from 'models/form';
import { selectAjaxLoader } from 'features/app/appSelectors';

interface CreateGDSDialogProps {
  open: boolean;
  onClose: (reload: boolean) => void;
  deliveryPointId: string;
}

interface CreateGDSState {
  name: string;
}

// constants
const I18N_ENTITY_ROOT = `module.${AppModule.EAI}.deliveryPoints`;
const I18N_COMPONENT_ROOT = 'dialogs.createGDSDialog';

const initialState: CreateGDSState = {
  name: ''
};

const inputs: FormInput[] = [
  {
    id: 'name',
    validation: {
      type: YupType.STRING,
      methods: [
        { methodType: 'required', args: ['DeliveryPointGDSName-required'] },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.STRING_256_MAX_LENGTH,
            'DeliveryPointGDSName-max'
          ]
        }
      ]
    }
  }
];

// ===============================|| CREATE GDS DIALOG ||=============================== //

const CreateGDSDialog = ({
  open,
  onClose,
  deliveryPointId
}: CreateGDSDialogProps): ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const handleSave = () => handleSubmit();

  const etagList = useAppSelector(selectEaiEtagList);
  const operationInProgress = useAppSelector(selectAjaxLoader);

  const { getFieldProps, touched, errors, handleSubmit } = useFormik({
    initialValues: initialState,
    validationSchema: createValidationSchema(inputs),
    onSubmit: (values) => {
      const requestBody: GDSForDeliveryPointNameInterface = {
        name: values.name
      };

      dispatch(
        registerGDSToDeliveryPoint(
          deliveryPointId,
          requestBody,
          etagList.deliveryPoint,
          () => {
            handleCloseWithReload();
          }
        )
      );
    },
    validateOnChange: false
  });

  const getControlProps = (name: string, type: InputType, options?: any[]) => {
    return {
      ...createFormControlProps({
        name,
        type,
        touched,
        errors,
        codebooks: [],
        module: AppModule.EAI,
        entityRoot: I18N_ENTITY_ROOT,
        options
      }),
      ...getFieldProps({ name, type })
    };
  };

  const handleCloseWithReload = () => {
    onClose(true);
  };

  const handleCloseWithoutReload = () => onClose(false);

  return (
    <Dialog
      open={open}
      onClose={handleCloseWithoutReload}
      aria-labelledby='create-gds-dialog-title'
      maxWidth='xs'
      fullWidth
    >
      <>
        <DialogTitle id='dialog-title'>
          {t(`${I18N_COMPONENT_ROOT}.title`)}
        </DialogTitle>

        <Divider
          sx={{
            opacity: 1,
            borderColor:
              theme.palette.mode === 'dark'
                ? theme.palette.dark.light + 15
                : theme.palette.grey[200]
          }}
        />

        {open ? (
          <DialogContent>
            <FormControl
              {...getControlProps('name', InputType.TEXT_FIELD)}
              autofocus={true}
            />
          </DialogContent>
        ) : (
          <Loader />
        )}

        <DialogActions sx={{ pr: 2.5 }}>
          <Button
            sx={{ color: theme.palette.error.dark }}
            onClick={handleCloseWithoutReload}
            color='secondary'
            disabled={operationInProgress}
          >
            {t(`${I18N_COMPONENT_ROOT}.backButtonTitle`)}
          </Button>
          <Button
            variant='contained'
            size='small'
            onClick={handleSave}
            disabled={operationInProgress}
          >
            {t(`${I18N_COMPONENT_ROOT}.submitButtonTitle`)}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};

export default CreateGDSDialog;
